import moment from 'moment-timezone';
import { v4 as uuidv4 } from 'uuid';
import { atsDataProvider } from '../dataProviders/atsDataProvider';
import {
  ATS_CUSTOMER_LINK_ALL_TEMPLATE_ID,
  ATS_CUSTOMER_LINK_RED_ORANGE_WITH_ALARMCENTER_TEMPLATE_ID,
  BHVK_ATS_CALL_TASK_TEMPLATE_ID,
  BHVK_ATS_ACKNOWLEDGE_TASK_TEMPLATE_ID,
  DEVICE_REGISTRATION_API_URL,
  XGAC_ATS_CUSTOMER_LINK_GREEN_TEMPLATE_ID,
} from '../config';
import { httpClient } from './httpClient';

const WORLD_POLYGON = [
  [
    [-157.7159643793944, 75.59255675711492],
    [-77.41423910275202, -56.135093116484946],
    [52.54265929181204, -41.204006875740966],
    [74.65472857088832, 69.84369594156016],
    [-157.7159643793944, 75.59255675711492],
  ],
];

const assetGroupRef = (id: string) => ({ _id: id, _ref: 'AssetGroup' as const });

const notInAssetGroupsFilter = (assetGroupIds: string[]) => ({
  operands: [
    {
      type: 'operator/not',
      operand: {
        type: 'constraint/assetGroup',
        groups: assetGroupIds.map(assetGroupRef),
      },
    },
  ],
  type: 'operator/or',
});

const inAssetGroupFilter = (assetGroupId: string) => ({
  operands: [{
    type: 'constraint/assetGroup',
    groups: [assetGroupRef(assetGroupId)],
  }],
  type: 'operator/and',
});

type CreateResource = (resource: string, params: { data: Record<string, any> }) => Promise<{ data: Record<string, any> }>;

const createAssetGroup = async (
  create: CreateResource,
  customerId: string,
  name: string,
  description: string,
) => {

  const result = await create('asset-groups', {
    data: {
      customer: customerId,
      name,
      description,
      assets: [],
    },
  });
  return result.data.id as string;

};

const createPeerAlertProcessTemplate = async ({
  customerId,
  processName,
  responderGroupId,
  customMessage,
}: {
  customerId: string;
  processName: string;
  responderGroupId: string;
  customMessage: string;
}) => {

  const result = await atsDataProvider.create('process-templates', {
    data: {
      xgacCustomerId: customerId,
      processName,
      description: 'Iedereen die beschikbaar is wordt direct opgeroepen.',
      scheduleTasks: [
        {
          id: uuidv4(),
          ifScript: null,
          metaScript: {
            name: 'getAssets',
            meta: {
              filter: {},
              xgacFilter: {
                request: {
                  'groups:equals': responderGroupId,
                },
                result: {
                  available: '==true',
                  assetId: '!=%alarmAssetId%',
                },
              },
            },
          },
          metaData: {
            afterCallBehaviour: 'redirect_call_helper',
            forceLocale: 'nl',
            customMessage,
          },
          taskTemplate: BHVK_ATS_CALL_TASK_TEMPLATE_ID,
          delay: 0,
        },
        {
          id: uuidv4(),
          ifScript: null,
          metaScript: { name: 'void' },
          metaData: null,
          taskTemplate: BHVK_ATS_ACKNOWLEDGE_TASK_TEMPLATE_ID,
          delay: 86400000,
        },
      ],
    },
  });

  const data = result.data as Record<string, unknown>;
  const templateId = data.id || data._id;
  if (!templateId || typeof templateId !== 'string') {

    throw new Error(`Failed to create process template "${processName}"`);

  }
  return templateId;

};

const createBhvkQr = async (customerId: string, bhvGroupId: string) => {

  const { json: template } = await httpClient(`${DEVICE_REGISTRATION_API_URL}/v3/deployment/code/template`, {
    method: 'POST',
  }, {
    name: 'Default QR template',
    xgacCustomerId: customerId,
    pages: [
      { name: 'register_verify', visible: false, meta: {} },
      { name: 'register_available', visible: false, meta: {} },
      { name: 'register_information', visible: false, meta: {} },
      { name: 'register_terms', visible: false, meta: {} },
      { name: 'register_privacy', visible: false, meta: {} },
    ],
  });

  const templateId = template._id || template.id;
  if (!templateId) {

    throw new Error('Failed to create BHVK QR template');

  }
  await Promise.all([
    httpClient(`${DEVICE_REGISTRATION_API_URL}/v3/deployment/code`, {
      method: 'POST',
    }, {
      codeTemplate: templateId,
      limit: 0,
      variables: [
        { key: 'register_external_id', locked: false, value: { type: 'string', data: '' } },
        { key: 'register_personal_firstname', locked: false, value: { type: 'string', data: '' } },
        { key: 'register_personal_lastname', locked: false, value: { type: 'string', data: '' } },
        { key: 'register_user_email', locked: false, value: { type: 'string', data: '' } },
        { key: 'register_personal_phone_country', locked: false, value: { type: 'string', data: '' } },
        { key: 'register_personal_phone_number', locked: false, value: { type: 'string', data: '' } },
        { key: 'register_personal_available', locked: false, value: { type: 'boolean', data: true } },
      ],
    }),
    ...[
      { actionTemplate: 'assign_customer', order: 1, xgacValue: customerId },
      { actionTemplate: 'asset_group', order: 2, xgacValue: [bhvGroupId] },
      { actionTemplate: 'user_restriction_group', order: 3, xgacValue: [] },
      { actionTemplate: 'config_group', order: 4, xgacValue: ['BHV-Knop.nl'] },
      { actionTemplate: 'create_user', order: 5, xgacValue: null },
    ].map((action) => httpClient(`${DEVICE_REGISTRATION_API_URL}/v3/deployment/action`, {
      method: 'POST',
    }, { codeTemplate: templateId, ...action })),
  ]);

};

export const bhvkCustomerCreateDefaults = async (
  customerId: string,
  createXgacResource: CreateResource,
) => {

  // asset-groups (and some other creates) use session currentCustomer, not body.customer
  const previousCustomer = sessionStorage.getItem('currentCustomer');
  sessionStorage.setItem('currentCustomer', JSON.stringify({ label: customerId, value: customerId }));

  try {

    await createXgacResource('zones', {
      data: {
        customer: customerId,
        name: 'Opvolging wereld',
        description: '',
        type: 'default',
        location: {
          coordinates: WORLD_POLYGON,
          type: 'Polygon',
          properties: {
            accuracy: 0,
            address: { formattedAddress: '' },
          },
        },
      },
    });

    const [bhvKnoppenId, bhvGroupId, paniekKnoppenId, aitGroupId] = await Promise.all([
      createAssetGroup(
        createXgacResource,
        customerId,
        'BHV-Knoppen',
        'Deze groep bevat alle hardware BHV-Knoppen besteld via bhv-knop.nl',
      ),
      createAssetGroup(
        createXgacResource,
        customerId,
        'BHV',
        'BHV-ers die onderling worden opgeroepen bij een BHV-Knop alarm',
      ),
      createAssetGroup(
        createXgacResource,
        customerId,
        'Paniekknoppen',
        'Deze groep bevat alle hardware paniekknoppen',
      ),
      createAssetGroup(
        createXgacResource,
        customerId,
        'AIT',
        'AIT/AOT-ers die onderling worden opgeroepen bij een paniekknop alarm',
      ),
      createAssetGroup(
        createXgacResource,
        customerId,
        'Basistations',
        'Deze groep bevat alle basistations',
      ),
    ]);

    const buttonGroupIds = [bhvKnoppenId, paniekKnoppenId];
    const notInButtonGroups = notInAssetGroupsFilter(buttonGroupIds);

    await Promise.all([
      createXgacResource('observation-triggers', {
        data: {
          customer: customerId,
          name: 'BHV-Knop ingedrukt',
          eventCodes: ['ALARM_RED'],
          description: 'De BHV-Knop is ingedrukt',
          status: 'active',
          actions: [{
            config: {
              severity: 'red',
              type: 'other',
              procedure: null,
              name: 'BHV-Knop ingedrukt',
            },
            type: 'alarm',
          }],
          filter: inAssetGroupFilter(bhvKnoppenId),
        },
      }),
      createXgacResource('observation-triggers', {
        data: {
          customer: customerId,
          name: 'Paniekknop ingedrukt',
          eventCodes: ['ALARM_RED'],
          description: 'De paniekknop is ingedrukt',
          status: 'active',
          actions: [{
            config: {
              severity: 'red',
              type: 'panic',
              procedure: null,
              name: 'Paniekknop ingedrukt',
            },
            type: 'alarm',
          }],
          filter: inAssetGroupFilter(paniekKnoppenId),
        },
      }),
      createXgacResource('observation-triggers', {
        data: {
          customer: customerId,
          name: 'Alarm - Mandown - bewusteloos',
          eventCodes: ['MANDOWN'],
          description: 'Dit alarm is geactiveerd doordat de gebruiker niet voldoende heeft bewogen.',
          status: 'active',
          actions: [{
            config: {
              severity: 'red',
              type: 'mandown',
              // eslint-disable-next-line max-len
              procedure: '1. **Oorzaak!**: Onvoldoende beweging.  \n2. **DIRECT MELDEN!** “Meldkamer, waarmee kan ik u van dienst zijn?”  \n3. Hoor je dat er hulp nodig is, klik op "Help".  \n4. Anders, klik op "Verificatie”.  \n5. Volg belvolgorde hieronder.  \n6. Bel tot max. 3 keer totdat je iemand hebt gesproken.',
              name: 'Mandown - bewusteloos',
            },
            type: 'alarm',
          }],
          filter: notInButtonGroups,
        },
      }),
      createXgacResource('observation-triggers', {
        data: {
          customer: customerId,
          name: 'Alarm - Paniekalarm',
          eventCodes: ['ALARM_RED'],
          description: 'Rode knop ingedrukt',
          status: 'active',
          actions: [{
            config: {
              severity: 'red',
              type: 'panic',
              // eslint-disable-next-line max-len
              procedure: '1. **NIET SPREKEN!**  \n2. Luister gesprek uit.  \n3. Wanneer je maximaal. 30 sec niets hoort verbreek dan verbinding.  \n4. Hoor je dat er hulp nodig is, klik op "Help".  \n5. Anders, klik op "Verificatie”.  \n6. Volg belvolgorde hieronder.  \n7. Bel tot max. 3 keer totdat je iemand hebt gesproken.',
              name: 'Paniek alarm',
            },
            type: 'alarm',
          }],
          filter: notInButtonGroups,
        },
      }),
      createXgacResource('observation-triggers', {
        data: {
          customer: customerId,
          name: 'Alarm - Assistentievraag',
          eventCodes: ['ALARM_ORANGE'],
          description: 'Oranje knop ingedrukt',
          status: 'active',
          actions: [{
            config: {
              severity: 'orange',
              type: 'assistance',
              // eslint-disable-next-line max-len
              procedure: '1. **DIRECT MELDEN!** “Meldkamer, waarmee kan ik u van dienst zijn?”.  \n2. Hoor je dat er hulp nodig is, klik op "Help".  \n3. Anders, klik op "Verificatie”.  \n4. Volg belvolgorde hieronder.  \n5. Bel tot max. 3 keer totdat je iemand hebt gesproken.',
              name: 'Assistentiealarm',
            },
            type: 'alarm',
          }],
          filter: notInButtonGroups,
        },
      }),
      createXgacResource('observation-triggers', {
        data: {
          customer: customerId,
          name: 'Alarm - Testmelding',
          eventCodes: ['ALARM_GREEN'],
          description: 'Groene knop ingedrukt',
          status: 'active',
          actions: [{
            config: {
              severity: 'green',
              type: 'test',
              // eslint-disable-next-line max-len
              procedure: '1\\. De gebruiker heeft een systeem testmelding verstuurd waarvoor geen verdere actie nodig is.  \n2\\. Gebruiker dient commentaarveld in webportaal in te (laten) vullen.',
              name: 'Test Melding',
            },
            type: 'alarm',
          }],
          filter: notInButtonGroups,
        },
      }),
    ]);

    // eslint-disable-next-line max-len
    const bhvCustomMessage = "Hallo, BHV-knop ingedrukt. %{message} op locatie %{asset} op %{address}. Toets 5 om de melding te accepteren en te spreken met de opgeroepen BHV'ers. Hallo, BHV-knop ingedrukt. %{message} op locatie %{asset} op %{address}. Toets 5 om de melding te accepteren en te spreken met de opgeroepen BHV'ers.";
    // eslint-disable-next-line max-len
    const aitCustomMessage = "Hallo, paniekknop ingedrukt. %{message} op locatie %{asset} op %{address}. Toets 5 om de melding te accepteren en te spreken met de opgeroepen AOT'ers. Hallo, paniekknop ingedrukt. %{message} op locatie %{asset} op %{address}. Toets 5 om de melding te accepteren en te spreken met de opgeroepen AOT'ers.";

    const [bhvProcessTemplateId, aitProcessTemplateId] = await Promise.all([
      createPeerAlertProcessTemplate({
        customerId,
        processName: 'Onderling melden voor BHV-Knop.nl, 1 fase - groep BHV',
        responderGroupId: bhvGroupId,
        customMessage: bhvCustomMessage,
      }),
      createPeerAlertProcessTemplate({
        customerId,
        processName: 'Onderling melden voor paniekknop, 1 fase - groep AIT',
        responderGroupId: aitGroupId,
        customMessage: aitCustomMessage,
      }),
    ]);

    const nextWeek = moment.tz('Europe/Amsterdam').startOf('isoWeek').add(1, 'week').toISOString();

    await Promise.all([
      atsDataProvider.create('customer-links', {
        data: {
          xgacCustomerId: customerId,
          xgacGroups: [bhvKnoppenId],
          alarmLevels: { green: false, red: true, orange: true },
          processTemplate: bhvProcessTemplateId,
        },
      }),
      atsDataProvider.create('customer-links', {
        data: {
          xgacCustomerId: customerId,
          xgacGroups: [paniekKnoppenId],
          alarmLevels: { green: false, red: true, orange: true },
          processTemplate: aitProcessTemplateId,
        },
      }),
      atsDataProvider.create('customer-links', {
        data: {
          xgacCustomerId: customerId,
          xgacGroups: [-2],
          notXgacGroups: buttonGroupIds,
          alarmLevels: { green: false, red: true, orange: true },
          processTemplate: ATS_CUSTOMER_LINK_RED_ORANGE_WITH_ALARMCENTER_TEMPLATE_ID,
        },
      }),
      atsDataProvider.create('customer-links', {
        data: {
          xgacCustomerId: customerId,
          xgacGroups: [-2],
          notXgacGroups: buttonGroupIds,
          alarmLevels: { green: true, red: false, orange: false },
          processTemplate: XGAC_ATS_CUSTOMER_LINK_GREEN_TEMPLATE_ID,
        },
      }),
      atsDataProvider.create('customer-links', {
        data: {
          xgacCustomerId: customerId,
          xgacGroups: [-2],
          notXgacGroups: buttonGroupIds,
          alarmLevels: { green: true, red: true, orange: true },
          processTemplate: ATS_CUSTOMER_LINK_ALL_TEMPLATE_ID,
        },
      }),
      createXgacResource('report-triggers', {
        data: {
          customer: customerId,
          naam: 'Alarmen - wekelijks',
          config: {
            type: 'alarms',
            excelFormat: 'default',
            severities: ['red'],
            alarmTypes: [],
            timeSeries: {
              granularity: { value: 1, type: 'hour' },
              timeframe: { value: 1, type: 'week', floorTo: 'day' },
            },
            filter: {},
            theme: 'bhvk',
          },
          channels: [{
            type: 'email',
            address: [],
            contactLists: [],
            getters: [],
          }],
          scheduleTrigger: {
            interval: { value: 1, type: 'week' },
            nextAt: nextWeek,
            lastAt: null,
            lastAttemptedAt: null,
          },
          eventTrigger: null,
          enabled: true,
          locale: 'nl',
        },
      }),
      createXgacResource('report-triggers', {
        data: {
          customer: customerId,
          naam: 'Inactiviteitsmonitor',
          config: {
            type: 'inactivityMonitor',
            assetGroups: buttonGroupIds.map(assetGroupRef),
            lastSeenThreshold: { value: 40, type: 'hour' },
            appMode: [],
            timeout: { value: 1, type: 'day' },
            theme: 'bhvk',
          },
          channels: [{
            type: 'email',
            address: [],
            contactLists: [],
            getters: [],
          }],
          scheduleTrigger: {
            interval: { value: 4, type: 'hour' },
            nextAt: nextWeek,
            lastAt: null,
            lastAttemptedAt: null,
          },
          eventTrigger: null,
          enabled: true,
          locale: 'nl',
        },
      }),
      createBhvkQr(customerId, bhvGroupId),
    ]);

  } finally {

    if (previousCustomer) {

      sessionStorage.setItem('currentCustomer', previousCustomer);

    } else {

      sessionStorage.removeItem('currentCustomer');

    }

  }

};
